import React from 'react'
import { Card, CardContent, Box, Typography, Chip, IconButton } from '@mui/material'
import { Edit, Trash2, GripVertical } from 'lucide-react'
import { Question } from '../../types/assessments'

interface QuestionCardProps {
  question: Question
  onEdit: () => void
  onDelete: () => void
  isDragging?: boolean
  showActions?: boolean
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onEdit,
  onDelete,
  isDragging = false,
  showActions = true
}) => {
  return (
    <Card
      sx={{
        mb: 2,
        transition: 'all 0.2s',
        transform: isDragging ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isDragging ? 6 : 2,
        opacity: isDragging ? 0.9 : 1,
        border: '1px solid #e0e0e0',
        '&:hover': {
          boxShadow: 4
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Drag Handle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'grab',
              color: '#999',
              '&:active': {
                cursor: 'grabbing'
              }
            }}
          >
            <GripVertical size={20} />
          </Box>

          {/* Question Content */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                Q{question.order}
              </Typography>
              <Chip label={question.type} size='small' sx={{ height: 20, fontSize: '0.7rem' }} />
            </Box>

            <Typography variant='body1' sx={{ fontWeight: 500, mb: 1 }}>
              {question.question}
            </Typography>

            {question.options && question.options.length > 0 && (
              <Box sx={{ mt: 1, mb: 1 }}>
                {question.options.map((option, index) => (
                  <Typography
                    key={index}
                    variant='body2'
                    color='text.secondary'
                    sx={{ ml: 2, mb: 0.5 }}
                  >
                    • {option}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          {/* Actions */}
          {showActions && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size='small' onClick={onEdit} color='primary'>
                <Edit size={18} />
              </IconButton>
              <IconButton size='small' onClick={onDelete} color='error'>
                <Trash2 size={18} />
              </IconButton>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
